import { useMemo } from "react"
import type { ApiEstimate } from "@/lib/api/types"
import { useEstimates } from "@/lib/api/use-estimates"
import { useOrganization } from "@/lib/organization/organization-context"
import { validateVinFormat } from "@/lib/vin/vin-validator"

// Finds existing estimates on a VIN across the staff user's active
// dealership AND active body shop, so a VIN already estimated at the
// dealership surfaces when the body shop re-scans it — and vice-versa.
// That hand-off is the case Sam flagged (Jul 20): a car comes through
// service, gets an estimate, moves to the body shop, and whoever scans it
// there has no idea it already exists. The old inline check looked at a
// single active team and missed it.
//
// Reuses GET /v1/estimates/team/{teamId} (#28, implemented); React Query
// caches per team, so both lists are usually already warm from the sidebar
// and the estimates page. useEstimates(null) is a no-op (enabled: !!teamId),
// so an absent slot costs nothing.
//
// Dev note (main re-impl): the durable version is a backend-computed
// "existing estimate" property returned by the VIN scan, scoped to the org
// group id (Dim's Jul-20 call) — it centralizes the org-visibility rules
// and drops the client-side fan-out. This hook is the design-branch stand-in.

const MAX_MATCHES = 3

export function useVinDuplicates(rawVin: string): ApiEstimate[] {
  const { activeDealershipId, activeBodyShopId } = useOrganization()
  const dealership = useEstimates(activeDealershipId ?? null)
  const bodyShop = useEstimates(activeBodyShopId ?? null)

  const vin = rawVin.replace(/[^A-HJ-NPR-Z0-9]/gi, "").toUpperCase()

  return useMemo(() => {
    if (vin.length !== 17 || !validateVinFormat(vin)) return []
    // Merge both teams' estimates, deduping by id (the same estimate can
    // appear in both lists when the two slots resolve to one team).
    const byId = new Map<string, ApiEstimate>()
    for (const e of [...(dealership.data ?? []), ...(bodyShop.data ?? [])]) {
      if (e.isActive === false) continue
      if (e.vehicle?.vin?.toUpperCase() !== vin) continue
      byId.set(String(e.id), e)
    }
    return [...byId.values()]
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, MAX_MATCHES)
  }, [vin, dealership.data, bodyShop.data])
}
